import React, { useState, useEffect } from 'react'
import { Trash2, Edit, Plus, X, Download, Check } from 'lucide-react'
import * as api from '../../app/axios/api.jsx'
import { getCookie } from '../../app/axios/CookieConfig'

const LaptopIssuePage = () => {
  const userName = getCookie('name')
  const [laptops, setLaptops] = useState([])
  const [showForm, setShowForm] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [loading, setLoading] = useState(false)
  const [showTodayOnly, setShowTodayOnly] = useState(true)

  // Inline editing states
  const [editingId, setEditingId] = useState(null)
  const [editData, setEditData] = useState({})

  // Get current date and time in required format
  const getCurrentDateTime = () => {
    const now = new Date()
    const date = now.toISOString().split('T')[0]
    const time = now.toTimeString().slice(0, 5)
    return { date, time }
  }

  const { date: currentDate, time: currentTime } = getCurrentDateTime()

  const [formData, setFormData] = useState({
    issue_date: currentDate,
    system_number: '',
    issue_to: '',
    issue_time: currentTime,
    issue_by: userName,
    remark: '',
  })

  useEffect(() => {
    fetchLaptops()
  }, [])

  const fetchLaptops = async () => {
    setLoading(true)
    try {
      const res = await api.fetchLaptop()
      setLaptops(res.data || [])
    } catch (error) {
      console.error('Error fetching laptops:', error)
      setLaptops([])
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = async () => {
    try {
      const issueDateTime = `${formData.issue_date}T${formData.issue_time}:00Z`

      const payload = {
        issue_date: issueDateTime,
        system_number: parseInt(formData.system_number),
        issue_to: formData.issue_to,
        issue_time: issueDateTime,
        issue_by: formData.issue_by,
        remark: formData.remark,
      }
      await api.createLaptop(payload)
      const { date, time } = getCurrentDateTime()
      setFormData({
        issue_date: date,
        system_number: '',
        issue_to: '',
        issue_time: time,
        issue_by: userName,
        remark: '',
      })
      setShowForm(false)
      fetchLaptops()
    } catch (error) {
      console.error('Error creating laptop issue:', error)
    }
  }

  // Start inline editing
  const startEdit = (laptop) => {
    const now = new Date()
    const hours = String(now.getHours()).padStart(2, '0')
    const minutes = String(now.getMinutes()).padStart(2, '0')
    const currentTime = `${hours}:${minutes}`

    setEditingId(laptop.id)
    setEditData({
      returned_by: laptop.returned_by || '',
      received_at: currentTime,
      received_by: userName,
      remark: laptop.remark || '',
    })
  }

  // Cancel inline editing
  const cancelEdit = () => {
    setEditingId(null)
    setEditData({})
  }

  // Save inline edit
  const saveEdit = async () => {
    try {
      const now = new Date()
      const currentDate = now.toISOString().split('T')[0]
      const receivedDateTime = `${currentDate}T${editData.received_at}:00Z`

      const payload = {
        returned_by: editData.returned_by,
        received_at: receivedDateTime,
        remark: editData.remark,
        received_by: editData.received_by,
      }

      await api.updateLaptop(editingId, payload)
      setEditingId(null)
      setEditData({})
      fetchLaptops()
    } catch (error) {
      console.error('Error updating laptop:', error)
    }
  }

  const handleDelete = async (id) => {
    try {
      await api.deleteLaptop(id)
      setDeleteConfirm(null)
      fetchLaptops()
    } catch (error) {
      console.error('Error deleting laptop:', error)
    }
  }

  // Filter laptops based on today's date
  const filteredLaptops = showTodayOnly
    ? laptops.filter((laptop) => {
        const issueDate = new Date(laptop.issue_date)
          .toISOString()
          .split('T')[0]
        return issueDate === currentDate
      })
    : laptops

  // CSV Download Function
  const downloadCSV = () => {
    const headers = [
      'System No',
      'Issue Date',
      'Issue To',
      'Issue Time',
      'Issue By',
      'Remark',
      'Returned By',
      'Received At',
      'Received By',
    ]

    const csvData = filteredLaptops.map((laptop) => [
      laptop.system_number,
      laptop.issue_date,
      laptop.issue_to,
      laptop.issue_time,
      laptop.issue_by || '-',
      laptop.remark || '-',
      laptop.returned_by || '-',
      laptop.received_at || '-',
      laptop.received_by || '-',
    ])

    const csvContent = [
      headers.join(','),
      ...csvData.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute(
      'download',
      `laptop_issues_${new Date().toISOString().split('T')[0]}.csv`,
    )
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 md:p-8'>
      <div className='max-w-7xl mx-auto'>
        {/* Header */}
        <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6'>
          <h1 className='text-2xl md:text-3xl font-bold text-gray-800'>
            Laptop Issue Management
          </h1>
          <div className='flex flex-wrap gap-2'>
            {/* Toggle Switch */}
            <button
              onClick={() => setShowTodayOnly(!showTodayOnly)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                showTodayOnly
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                  : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
              }`}
            >
              <span className='text-sm font-medium'>
                {showTodayOnly ? "Today's Records" : 'All Records'}
              </span>
            </button>
            <button
              onClick={downloadCSV}
              disabled={filteredLaptops.length === 0}
              className='flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed'
            >
              <Download size={20} />
              <span className='hidden sm:inline'>Download CSV</span>
            </button>
            <button
              onClick={() => setShowForm(true)}
              className='flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition'
            >
              <Plus size={20} />
              <span className='hidden sm:inline'>New Issue</span>
            </button>
          </div>
        </div>

        {/* Create Form Modal */}
        {showForm && (
          <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
            <div className='bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto'>
              <div className='flex justify-between items-center mb-4'>
                <h2 className='text-xl font-bold'>New Laptop Issue</h2>
                <button
                  onClick={() => setShowForm(false)}
                  className='text-gray-500 hover:text-gray-700'
                >
                  <X size={24} />
                </button>
              </div>
              <div className='space-y-4'>
                <div>
                  <label className='block text-sm font-medium mb-1'>
                    Issue Date
                  </label>
                  <input
                    type='date'
                    required
                    value={formData.issue_date}
                    onChange={(e) =>
                      setFormData({ ...formData, issue_date: e.target.value })
                    }
                    className='w-full border rounded-lg px-3 py-2'
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium mb-1'>
                    System Number
                  </label>
                  <input
                    type='number'
                    required
                    value={formData.system_number}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        system_number: e.target.value,
                      })
                    }
                    className='w-full border rounded-lg px-3 py-2'
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium mb-1'>
                    Issue To
                  </label>
                  <input
                    type='text'
                    required
                    value={formData.issue_to}
                    onChange={(e) =>
                      setFormData({ ...formData, issue_to: e.target.value })
                    }
                    className='w-full border rounded-lg px-3 py-2'
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium mb-1'>
                    Issue Time
                  </label>
                  <input
                    type='time'
                    required
                    value={formData.issue_time}
                    onChange={(e) =>
                      setFormData({ ...formData, issue_time: e.target.value })
                    }
                    className='w-full border rounded-lg px-3 py-2'
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium mb-1'>
                    Remark
                  </label>
                  <textarea
                    value={formData.remark}
                    onChange={(e) =>
                      setFormData({ ...formData, remark: e.target.value })
                    }
                    className='w-full border rounded-lg px-3 py-2'
                    rows='3'
                  />
                </div>
                <div className='flex gap-2'>
                  <button
                    onClick={handleCreate}
                    className='flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700'
                  >
                    Submit
                  </button>
                  <button
                    onClick={() => setShowForm(false)}
                    className='flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400'
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation */}
        {deleteConfirm && (
          <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
            <div className='bg-white rounded-lg p-6 w-full max-w-sm'>
              <h3 className='text-lg font-bold mb-4'>Confirm Delete</h3>
              <p className='text-gray-600 mb-6'>
                Are you sure you want to delete this laptop issue record?
              </p>
              <div className='flex gap-2'>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  className='flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700'
                >
                  Delete
                </button>
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className='flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400'
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Data Table */}
        <div className='bg-white rounded-lg shadow overflow-hidden'>
          {loading ? (
            <div className='p-8 text-center text-gray-500'>Loading...</div>
          ) : filteredLaptops.length === 0 ? (
            <div className='p-8 text-center text-gray-500'>
              {showTodayOnly
                ? 'No records found for today'
                : 'No records found'}
            </div>
          ) : (
            <div className='overflow-x-auto'>
              <table className='w-full'>
                <thead className='bg-gray-100 border-b'>
                  <tr>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase'>
                      System No
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase'>
                      Issue Date
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase'>
                      Issue To
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase'>
                      Issue Time
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase'>
                      Issue By
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase'>
                      Remark
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase'>
                      Returned By
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase'>
                      Received At
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase'>
                      Received By
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase'>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-200'>
                  {filteredLaptops.map((laptop) => (
                    <tr
                      key={laptop.id}
                      className={
                        editingId === laptop.id
                          ? 'bg-blue-50'
                          : 'hover:bg-gray-50'
                      }
                    >
                      <td className='px-4 py-3 text-sm'>
                        {laptop.system_number}
                      </td>
                      <td className='px-4 py-3 text-sm'>{laptop.issue_date}</td>
                      <td className='px-4 py-3 text-sm'>{laptop.issue_to}</td>
                      <td className='px-4 py-3 text-sm'>
                        {laptop.issue_time
                          ? laptop.issue_time.substring(11, 16) // HH:MM format
                          : '--'}
                      </td>

                      <td className='px-4 py-3 text-sm'>
                        {laptop.issue_by_2 || '-'}
                      </td>
                      <td className='px-4 py-3 text-sm'>
                        {editingId === laptop.id ? (
                          <input
                            type='text'
                            value={editData.remark}
                            onChange={(e) =>
                              setEditData({
                                ...editData,
                                remark: e.target.value,
                              })
                            }
                            className='w-full border rounded px-2 py-1 text-sm'
                            placeholder='Remark'
                          />
                        ) : (
                          laptop.remark || '-'
                        )}
                      </td>
                      <td className='px-4 py-3 text-sm'>
                        {editingId === laptop.id ? (
                          <input
                            type='text'
                            value={editData.returned_by}
                            onChange={(e) =>
                              setEditData({
                                ...editData,
                                returned_by: e.target.value,
                              })
                            }
                            className='w-full border rounded px-2 py-1 text-sm'
                            placeholder='Returned By'
                            required
                          />
                        ) : (
                          laptop.returned_by || '-'
                        )}
                      </td>
                      <td className='px-4 py-3 text-sm'>
                        {laptop.received_at
                          ? laptop.received_at.substring(11, 16) // "HH:MM"
                          : '-'}
                      </td>

                      <td className='px-4 py-3 text-sm'>
                        {laptop.received_by_2 || '-'}
                      </td>
                      <td className='px-4 py-3 text-sm'>
                        {editingId === laptop.id ? (
                          <div className='flex gap-2'>
                            <button
                              onClick={saveEdit}
                              className='text-green-600 hover:text-green-800'
                              title='Save'
                            >
                              <Check size={18} />
                            </button>
                            <button
                              onClick={cancelEdit}
                              className='text-gray-600 hover:text-gray-800'
                              title='Cancel'
                            >
                              <X size={18} />
                            </button>
                          </div>
                        ) : (
                          <div className='flex gap-2'>
                            <button
                              onClick={() => startEdit(laptop)}
                              className='text-blue-600 hover:text-blue-800'
                              title='Edit'
                            >
                              <Edit size={18} />
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(laptop.id)}
                              className='text-red-600 hover:text-red-800'
                              title='Delete'
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default LaptopIssuePage
